import type { Token } from '@stripe/stripe-js'
import { useRouter } from 'next/router'
import { useState } from 'react'
import SvgCMANew from '#/assets/images/icons/cma_new.svg'
import Button from '#/components/Button'
import Modal from '#/components/Modal'
import type { SectionsConfig } from '#/containers/PDF/FullProperty/components/Report/types'
import CustomizeReport from '#/containers/ReportCustomize'
import { logEvent } from '#/services/analytics'
import { RequestReport } from '#/store/actions/workspace'
import { reportFrontUrl } from '#/utils/report'
import { urlWithQuery } from '#/utils/url'
import PaymentInformation from './PaymentInformation'

/**
 * Purchasing Report Flow
 * - If it has enough roles
 *   - It should open the modal to 'Customize'
 *   - After selecting done on the 'Customize' modal it should request to make the report
 * - Otherwise, show PaymentInformation Modal
 *   - It should call "requestReports" with token and recipients after clicking Payment
 *   - If user isn't authenticated, it should show "SignUp" checkbox. It should show Sign Up Modal
 *   - If user hasn't enough role, it should show "Upgrade" link, it should show upgrade modal
 */

type ReportsPurchaseProps = {
  authenticated: boolean
  hasRole: boolean
  property: TopHap.Property
  reportType: TopHap.ReportType
  user: TopHap.UserInfo
  requestReport: typeof RequestReport
  showCTAPopup: (visible: boolean, requireRole?: TopHap.UserRole) => void
}

type ModalState = {
  show: boolean
  type: 'Payment' | 'Customize'
}

const ReportsPurchase: React.FC<ReportsPurchaseProps> = ({
  authenticated,
  hasRole,
  property,
  reportType,
  user,
  requestReport,
  showCTAPopup
}) => {
  const [modal, showModal] = useState<ModalState>({
    show: false,
    type: 'Payment'
  })
  const [loading, setLoading] = useState(false)
  const [reportConfig, setReportConfig] = useState<SectionsConfig | undefined>()
  const router = useRouter()

  const closeModal = () => {
    showModal({
      show: false,
      type: 'Payment'
    })
  }

  const handleClick = async () => {
    if (hasRole) {
      // setLoading(true)
      // const params = await requestReportParams(reportType)
      // requestReport({
      //   payload: params,
      //   callback: () => setLoading(false)
      // })
      showModal({
        show: true,
        type: 'Customize'
      })
    } else {
      showModal({
        ...modal,
        show: true
      })
    }
  }

  const handleConfigChange = async (config: SectionsConfig) => {
    setReportConfig(config)
    if (hasRole) {
      setLoading(true)
      showModal({
        show: false,
        type: 'Payment'
      })
      const params = await requestReportParams(reportType)
      logEvent('select_content', {
        content_type: 'property_detail',
        item_id: 'report',
        report_type: 'full_property_report',
        value: property.id,
        address: property.address.FullAddress
      })
      requestReport({
        payload: {
          ...params,
          clientPayload: { sectionsConfig: config }
        },
        callback: () => setLoading(false)
      })
    } else {
      showModal({
        show: true,
        type: 'Payment'
      })
    }
  }

  const requestReportParams = async (reportType: TopHap.ReportType):
  Promise<Pick<TopHap.Service.RequestReportRequest, 'frontendUrl' | 'kind' | 'propertyId'>> => (
    {
      frontendUrl: reportFrontUrl(reportType, property.id),
      kind: reportType,
      propertyId: property.id
    }
  )

  const handleUpgrade = () => {
    showCTAPopup(true, 'pro')
    closeModal()
  }

  const handleChangeModal = (modal: ModalState) => {
    showModal(modal)
  }

  const handleSubmit = async (
    reportType: TopHap.ReportType,
    token: Token,
    receiptEmail: string,
    recipients: string[],
    name: string,
    signUp: boolean
  ) => {
    setLoading(true)
    const params = await requestReportParams(reportType)

    requestReport({
      payload: {
        ...params,
        token,
        recipients,
        receiptEmail,
        clientPayload: { sectionsConfig: reportConfig }
      },
      callback: (err?: any) => {
        setLoading(false)
        if (!err) {
          closeModal()
          if (signUp && !authenticated) {
            router.push(urlWithQuery(router, { auth: 'signup', fr: 'true', email: receiptEmail, name }, true).href)
          }
        }
      }
    })
  }

  return (
    <>
      <Button
        className='h-10 rounded-lg bg-primary px-4 text-sm text-white'
        loading={hasRole && loading}
        onClick={handleClick}
      >
        <SvgCMANew className='mr-1 h-[18px] w-[18px]' />
        Download Report
      </Button>
      <Modal
        visible={modal.show}
        onClose={closeModal}
      >
        {modal.type === 'Payment' ? (
          <PaymentInformation
            authenticated={authenticated}
            loading={!hasRole && loading}
            property={property}
            reportType={reportType}
            onChangeModal={handleChangeModal}
            onSubmit={handleSubmit}
            onUpgrade={handleUpgrade}
          />
        ) : (
          <CustomizeReport
            property={property}
            reportType='PROPERTY_FULL'
            user={user}
            onChangeConfig={handleConfigChange}
          />
        )}

      </Modal>
    </>
  )
}

export default ReportsPurchase
